/* ============================================================
   The write outbox — every mutation as a SERIALIZABLE description of intent,
   plus the code that turns one back into an HTTP call.

   Why intent and not a closure: the queue has to survive a force-quit, so it
   gets written to disk (store/persist.ts). A `() => api.post(...)` thunk can't
   be. Each mutation therefore enqueues an OutboxOp — a plain object naming the
   call and its arguments — and the runner in store/api.tsx drains the queue
   IN ORDER, one entry at a time.

   Ordering is load-bearing, not incidental. A create must land before the patch
   that edits it; a chain advance must land before the next one. Serial delivery
   is what makes that true, and it's why a stuck entry blocks the queue behind it
   instead of being skipped.

   Two things every op carries deliberately:
     • `today` / `day` are captured WHEN THE USER ACTED, not when the entry
       drains — completing something at 23:59 offline must still record that day
       even if the queue flushes after midnight.
     • local temp ids (`tempId`, `snapshotId`) survive serialization, so a
       response that arrives after a restart still merges onto the right local
       row. Ids the SERVER needs to see are rewritten by remapIds once the create
       ahead of them is acknowledged.

   Known limit — delivery is at-least-once, not exactly-once. `habit.checkin`
   INCREMENTS the day's progress server-side and `task.complete`/`task.skip`
   ADVANCE a repeating task's chain, so a retry of a request the server actually
   processed (a 5xx or timeout that committed anyway) double-applies. The fix is
   an idempotency key the server dedupes on; until then dispositionFor keeps
   retries to cases that either provably didn't reach the handler or are worth
   the risk against permanent silent loss. See docs/offline-first.md.
   ============================================================ */
import type { HabitSchedule } from '@habishine/shared';

import { ApiError, type ApiClient } from '../api/client';
import type { ServerHabit, ServerProject, ServerTag, ServerTask, TaskResolution } from '../api/types';
import type { HueName } from '../hues';
import type { SubStep } from '../types';

/** Body of POST /v1/tasks — mirrors the backend createTaskSchema. */
export interface CreateTaskBody {
  name: string;
  hue: HueName;
  /** the project to file it under; null = unfiled */
  projectId: string | null;
  /** the tags to attach; [] = untagged */
  tagIds: string[];
  scheduledFor: string | null;
  subSteps: SubStep[];
  rrule: string | null;
  /** minutes since local midnight; null = all-day */
  remindAt: number | null;
  today: string;
  /** where the task lands in the manual order — sent so the server row and the
     optimistic one agree, rather than the task jumping on the next sync */
  sortOrder: number;
}

/** Body of POST /v1/projects — mirrors the backend createListSchema. */
export interface CreateProjectBody {
  name: string;
  hue: HueName;
}

/** Body of POST /v1/tags — mirrors the backend createTagSchema. A tag carries a
   name and its own accent hue; still no lifecycle (nothing to archive). */
export interface CreateTagBody {
  name: string;
  hue: HueName;
}

/** Body of POST /v1/habits — mirrors the backend createHabitSchema. */
export interface CreateHabitBody {
  /** where it lands in the habit list — a new habit appends */
  sortOrder?: number;
  name: string;
  hue: HueName;
  emoji?: string;
  target: number;
  schedule: HabitSchedule;
  startsOn: string;
}

/** One queued mutation. `kind` names the endpoint; everything else is its
   arguments. Must stay JSON round-trippable — no Dates, no functions. */
export type OutboxOp =
  /** create; `tempId` is the local row awaiting the server's id */
  | { kind: 'task.create'; tempId: string; body: CreateTaskBody }
  | {
      kind: 'task.patch';
      id: string;
      patch: {
        name?: string;
        hue?: HueName;
        /** null unfiles the task; a string files it under that project */
        projectId?: string | null;
        /** REPLACES the task's tags; [] clears them */
        tagIds?: string[];
        rrule?: string | null;
        remindAt?: number | null;
        subSteps?: SubStep[];
        /** a new manual position. One task moves per drag, so a reorder is one
           of these — see docs/task-ordering.md for why the rank is fractional. */
        sortOrder?: number;
      };
    }
  /** move a task to a day (or null = a backlog). Carries the manual rank for the
     list it is ARRIVING in, so the move and the placement are one round trip. */
  | { kind: 'task.reschedule'; id: string; scheduledFor: string | null; sortOrder?: number }
  /** complete; `snapshotId` is the local id holding the chain's history row */
  | { kind: 'task.complete'; id: string; today: string; snapshotId: string }
  | { kind: 'task.skip'; id: string; today: string; snapshotId: string }
  | { kind: 'task.reopen'; id: string }
  | { kind: 'task.unskip'; id: string }
  | { kind: 'task.delete'; id: string }
  | { kind: 'project.create'; tempId: string; body: CreateProjectBody }
  | { kind: 'project.patch'; id: string; patch: { name?: string; hue?: HueName } }
  /** archive / un-archive — a lifecycle move, its own route server-side */
  | { kind: 'project.archive'; id: string }
  | { kind: 'project.unarchive'; id: string }
  /** delete a project. The server unfiles its tasks in the same transaction, so
     nothing else needs to be queued for them. */
  | { kind: 'project.delete'; id: string }
  | { kind: 'tag.create'; tempId: string; body: CreateTagBody }
  | { kind: 'tag.patch'; id: string; patch: { name?: string; hue?: HueName } }
  /** delete a tag. The server strips it from its tasks in the same transaction,
     so nothing else needs to be queued for them — the same deal as project.delete.
     A tag has no archive counterpart: there is nothing to retire. */
  | { kind: 'tag.delete'; id: string }
  | { kind: 'habit.create'; tempId: string; body: CreateHabitBody }
  | {
      kind: 'habit.patch';
      id: string;
      patch: {
        name?: string;
        emoji?: string;
        hue?: HueName;
        target?: number;
        schedule?: HabitSchedule;
        /** a new manual position. One habit moves per drag, so a reorder is one
           of these — see docs/task-ordering.md. */
        sortOrder?: number;
      };
    }
  | { kind: 'habit.delete'; id: string }
  | { kind: 'habit.checkin'; id: string; day: string; today: string }
  | { kind: 'habit.uncheckin'; id: string; day: string; today: string }
  /** pin an item into a day's slot. `scheduleFirst` when the item is a backlog
     task that has to be moved onto the day before it can be pinned. */
  | { kind: 'pin.set'; day: string; itemType: 'habit' | 'task'; itemId: string; scheduleFirst: boolean }
  | { kind: 'pin.clear'; day: string };

export interface OutboxEntry {
  /** enqueue order — the queue drains ascending */
  seq: number;
  op: OutboxOp;
  /** count of attempts the SERVER actively rejected (5xx/429/…). Attempts that
     never reached it (offline) don't count, so being offline can never age a
     user's action out of the queue. */
  tries: number;
}

/** The merge callbacks the runner supplies — the same ones a live write uses, so
   an entry that drains ten minutes late lands in state exactly as it would have
   landed immediately. */
export interface OpHandlers {
  mergeTask(srv: ServerTask): void;
  mergeProject(srv: ServerProject): void;
  reconcileProject(oldId: string, srv: ServerProject): void;
  mergeTag(srv: ServerTag): void;
  reconcileTag(oldId: string, srv: ServerTag): void;
  mergeHabit(srv: ServerHabit): void;
  mergeResolution(res: TaskResolution, snapshotTempId: string): void;
  reconcileTask(oldId: string, srv: ServerTask): void;
  reconcileHabit(oldId: string, srv: ServerHabit): void;
}

/** A local id the server has now named. The runner rewrites the rest of the
   queue with it so a queued patch/pin stops pointing at a temp id. */
export interface IdMapping {
  from: string;
  to: string;
}

/** Send one op and merge its response. Returns an id mapping for a create (so
   entries behind it can be rewritten), else null. Throws whatever the client
   throws — the caller decides retry vs. drop. */
export async function performOp(
  api: ApiClient,
  op: OutboxOp,
  h: OpHandlers,
): Promise<IdMapping | null> {
  switch (op.kind) {
    case 'task.create': {
      const srv = await api.post<ServerTask>('/v1/tasks', op.body);
      h.reconcileTask(op.tempId, srv);
      return { from: op.tempId, to: srv.id };
    }
    case 'task.patch': {
      h.mergeTask(await api.patch<ServerTask>(`/v1/tasks/${op.id}`, op.patch));
      return null;
    }
    case 'task.reschedule': {
      h.mergeTask(
        await api.post<ServerTask>(`/v1/tasks/${op.id}/reschedule`, {
          scheduledFor: op.scheduledFor,
          ...(op.sortOrder === undefined ? {} : { sortOrder: op.sortOrder }),
        }),
      );
      return null;
    }
    case 'task.complete': {
      const res = await api.post<TaskResolution>(`/v1/tasks/${op.id}/complete`, {
        today: op.today,
      });
      h.mergeResolution(res, op.snapshotId);
      return null;
    }
    case 'task.skip': {
      const res = await api.post<TaskResolution>(`/v1/tasks/${op.id}/skip`, { today: op.today });
      h.mergeResolution(res, op.snapshotId);
      return null;
    }
    case 'task.reopen': {
      h.mergeTask(await api.post<ServerTask>(`/v1/tasks/${op.id}/reopen`));
      return null;
    }
    case 'task.unskip': {
      h.mergeTask(await api.post<ServerTask>(`/v1/tasks/${op.id}/unskip`));
      return null;
    }
    case 'task.delete': {
      await api.del(`/v1/tasks/${op.id}`);
      return null;
    }
    case 'project.create': {
      const srv = await api.post<ServerProject>('/v1/projects', op.body);
      h.reconcileProject(op.tempId, srv);
      return { from: op.tempId, to: srv.id };
    }
    case 'project.patch': {
      h.mergeProject(await api.patch<ServerProject>(`/v1/projects/${op.id}`, op.patch));
      return null;
    }
    case 'project.archive': {
      h.mergeProject(await api.post<ServerProject>(`/v1/projects/${op.id}/archive`));
      return null;
    }
    case 'project.unarchive': {
      h.mergeProject(await api.post<ServerProject>(`/v1/projects/${op.id}/unarchive`));
      return null;
    }
    case 'project.delete': {
      await api.del(`/v1/projects/${op.id}`);
      return null;
    }
    case 'tag.create': {
      // The server is idempotent on a case-folded name, so a tag typed into
      // being on two devices resolves to ONE row — and the mapping below points
      // this device's temp id at whichever row won.
      const srv = await api.post<ServerTag>('/v1/tags', op.body);
      h.reconcileTag(op.tempId, srv);
      return { from: op.tempId, to: srv.id };
    }
    case 'tag.patch': {
      h.mergeTag(await api.patch<ServerTag>(`/v1/tags/${op.id}`, op.patch));
      return null;
    }
    case 'tag.delete': {
      await api.del(`/v1/tags/${op.id}`);
      return null;
    }
    case 'habit.create': {
      const srv = await api.post<ServerHabit>('/v1/habits', op.body);
      h.reconcileHabit(op.tempId, srv);
      return { from: op.tempId, to: srv.id };
    }
    case 'habit.patch': {
      h.mergeHabit(await api.patch<ServerHabit>(`/v1/habits/${op.id}`, op.patch));
      return null;
    }
    case 'habit.delete': {
      await api.del(`/v1/habits/${op.id}`);
      return null;
    }
    case 'habit.checkin': {
      h.mergeHabit(
        await api.post<ServerHabit>(`/v1/habits/${op.id}/checkin`, {
          day: op.day,
          today: op.today,
        }),
      );
      return null;
    }
    case 'habit.uncheckin': {
      h.mergeHabit(
        await api.post<ServerHabit>(`/v1/habits/${op.id}/uncheckin`, {
          day: op.day,
          today: op.today,
        }),
      );
      return null;
    }
    case 'pin.set': {
      // A backlog task has to own the day before it can hold that day's slot
      // (mirrors logic.togglePin). Re-running the reschedule on a retry is
      // harmless — it sets the same day.
      if (op.scheduleFirst) {
        h.mergeTask(
          await api.post<ServerTask>(`/v1/tasks/${op.itemId}/reschedule`, {
            scheduledFor: op.day,
          }),
        );
      }
      await api.put(`/v1/pins/${op.day}`, { itemType: op.itemType, itemId: op.itemId });
      return null;
    }
    case 'pin.clear': {
      await api.del(`/v1/pins/${op.day}`);
      return null;
    }
  }
}

/** Rewrite one id inside a tag set, returning the SAME array when nothing
   changed — the callers lean on that to leave an untouched op untouched.

   The set can hold more than one temp id (two brand-new tags in one capture),
   and each is rewritten by its own mapping as its create is acknowledged. */
function remapTagIds<T extends string[] | undefined>(tagIds: T, from: string, to: string): T {
  if (!tagIds?.includes(from)) return tagIds;
  // A rewrite can collide with an id already in the set — two devices typing
  // `@errand` resolve to one server row — so dedupe on the way out.
  return [...new Set(tagIds.map((id) => (id === from ? to : id)))] as T;
}

/** Point an op at a server id in place of a local temp id. */
export function remapIds(op: OutboxOp, from: string, to: string): OutboxOp {
  if (from === to) return op;
  switch (op.kind) {
    // A create's tempId is local-only — the server never sees it, and it's the
    // mapping's source, so it must not be rewritten. pin.clear names a day.
    case 'task.create': {
      // The create's own tempId is the mapping's source and must not be
      // rewritten — but its BODY may point at a project or a TAG created moments
      // earlier and still queued, so those references do need remapping. Both
      // are the ordinary path rather than an edge case: filing a task into a
      // brand-new project, or tagging it `@brand-new`, is one gesture in the
      // capture sheet.
      const projectId = op.body.projectId === from ? to : op.body.projectId;
      const tagIds = remapTagIds(op.body.tagIds, from, to);
      return projectId === op.body.projectId && tagIds === op.body.tagIds
        ? op
        : { ...op, body: { ...op.body, projectId, tagIds } };
    }
    case 'project.create':
    case 'tag.create':
    case 'habit.create':
    case 'pin.clear':
      return op;
    case 'pin.set':
      return op.itemId === from ? { ...op, itemId: to } : op;
    case 'task.patch': {
      // Three ids can need rewriting here, independently: the task being
      // patched, the project it's being moved into, and any tag in the set it's
      // being given.
      const projectId = op.patch.projectId === from ? to : op.patch.projectId;
      const tagIds = remapTagIds(op.patch.tagIds, from, to);
      const patched =
        projectId === op.patch.projectId && tagIds === op.patch.tagIds
          ? op
          : { ...op, patch: { ...op.patch, projectId, tagIds } };
      return patched.id === from ? { ...patched, id: to } : patched;
    }
    default:
      // Every remaining variant carries `id: string` and nothing else keyed by
      // it, so overriding that one field is shape-preserving; the cast is only
      // needed because TS won't narrow a spread across a union.
      return op.id === from ? ({ ...op, id: to } as OutboxOp) : op;
  }
}

/** What to do with an entry whose send threw.

     retry          the request didn't reach the handler (offline, unauthorized,
                    build below the server's floor) —
                    wait and try again, forever if need be. Never counted, so a
                    long trip offline can't age a user's work out of the queue.
     retry-limited  the server answered with something transient (5xx / 429 /
                    timeout). Worth retrying, but counted: an entry the server
                    keeps rejecting must eventually let the queue behind it move.
     drop           the server refused it on its merits (validation, gone,
                    forbidden). Retrying can only fail identically. */
export type Disposition = 'retry' | 'retry-limited' | 'drop';

export function dispositionFor(err: unknown): Disposition {
  // Not an ApiError => fetch itself rejected, so no request was completed. This
  // is the offline case the outbox exists for.
  if (!(err instanceof ApiError)) return 'retry';
  const { status } = err;
  // 401 is either an expired token mid-refresh or a signed-out session; API
  // Gateway rejects before the handler, so nothing was applied. Worth waiting on
  // — the user may sign back in.
  if (status === 401) return 'retry';
  // 426: this build is below the server's minimum. Refused at the edge before
  // any handler, so nothing was applied — and the entry is valid, just early.
  // Wait for the new build to send it; dropping would discard the user's
  // offline work over a version check (backend/src/client-version.ts).
  if (status === 426) return 'retry';
  if (status === 408 || status === 425 || status === 429 || status >= 500) return 'retry-limited';
  return 'drop';
}

/** True when the server says the target no longer exists. Dropping such an entry
   is the correct outcome, not a divergence, so it needn't force a resync. */
export function isGone(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404;
}
